import React from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line
} from 'recharts'
import { TrendDataPoint } from '../lib/data-service'
import { cn } from '../lib/utils'

const WINDOW_OPTIONS = [4, 8, 12] as const

type WindowOption = (typeof WINDOW_OPTIONS)[number]

interface RevenueTrendChartProps {
  data: TrendDataPoint[] | undefined
  selectedWindow: WindowOption
  onWindowChange: (value: WindowOption) => void
  className?: string
}

const formatCurrency = (value: number) => {
  if (!Number.isFinite(value)) return '$0'
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatWeekLabel = (point: TrendDataPoint) => {
  if (point.fiscal_week_code) return point.fiscal_week_code
  if (point.week_start && point.week_end) {
    return `${point.week_start} – ${point.week_end}`
  }
  return `FW ${point.week}`
}

const tooltipFormatter = (value: number, _name: string, entry: any) => {
  const label = entry?.dataKey === 'revenue_ty' ? 'TY Revenue' : 'LY Revenue'
  return [formatCurrency(value), label]
}

const tooltipLabelFormatter = (_value: any, payload: any[]) => {
  if (!payload || payload.length === 0) return ''
  const point = payload[0].payload as TrendDataPoint
  return formatWeekLabel(point)
}

export function RevenueTrendChart({ data, selectedWindow, onWindowChange, className }: RevenueTrendChartProps) {
  const safeData = (data ?? []).map(point => {
    const revenueTy = Number(point.revenue_ty)
    const revenueLy = Number(point.revenue_ly)

    return {
      ...point,
      revenue_ty: Number.isFinite(revenueTy) ? revenueTy : 0,
      revenue_ly: Number.isFinite(revenueLy) ? revenueLy : 0
    }
  })

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(event.target.value, 10) as WindowOption
    if (WINDOW_OPTIONS.includes(value)) {
      onWindowChange(value)
    }
  }

  return (
    <div className={cn('bg-white border border-gray-200 rounded-xl shadow-sm', className)}>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Revenue Trend</h3>
          <p className="text-xs text-gray-500">Trailing performance vs. last year</p>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <label htmlFor="trend-window" className="text-gray-600">
            Window
          </label>
          <select
            id="trend-window"
            value={selectedWindow}
            onChange={handleSelectChange}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {WINDOW_OPTIONS.map(option => (
              <option key={option} value={option}>{option} weeks</option>
            ))}
          </select>
        </div>
      </div>

      <div className="px-2 py-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={safeData} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="week"
              tickFormatter={(_value, index) => {
                const point = safeData[index]
                if (!point) return ''
                if (selectedWindow <= 8 && point.fiscal_week_code) return point.fiscal_week_code
                return `FW ${String(point.week).slice(-2)}`
              }}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={tooltipLabelFormatter}
              contentStyle={{ borderRadius: 8, borderColor: '#e5e7eb' }}
            />
            <Legend
              verticalAlign="top"
              align="left"
              height={36}
              wrapperStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="revenue_ly" name="LY Revenue" fill="#d1d5db" maxBarSize={24} radius={[6, 6, 0, 0]} />
            <Line type="monotone" dataKey="revenue_ty" name="TY Revenue" stroke="#ec4899" strokeWidth={3} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default RevenueTrendChart
